import { useEffect, useState } from "react";
import { router } from "@inertiajs/react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, ArrowLeft, ArrowRight } from "lucide-react";
import posthog from "posthog-js";
import { useSubmissionForm } from "./hooks/useSubmissionForm";
import {
  StepIndicator,
  ImageUploadStep,
  CategoryStep,
  SubmissionTypeStep,
  DemographicsStep,
  ReviewStep,
} from "./components";

interface Props {
  user: { email: string };
  photo?: { id: number; photoUrl: string };
  errors?: Record<string, string | string[]>;
}

// Steps use absolute numbering (1-5).
// When hasPhoto, step 1 (image) is skipped: flow starts at 2.
const STEP_TITLES: Record<number, string> = {
  1: "Enviar Foto",
  2: "Escolher Categoria",
  3: "Tipo de Envio",
  4: "Público-Alvo",
  5: "Revisar sua Foto",
};

const STEP_DESCRIPTIONS: Record<number, string> = {
  1: "Escolha uma foto e ajuste o recorte",
  2: "Escolha o contexto onde deseja ser avaliado",
  3: "Escolha entre envio gratuito ou pago",
  4: "Especifique de quem deseja receber avaliações",
  5: "Revise todos os detalhes antes de enviar",
};

const FULL_STEPS = [
  { label: "Foto", description: "Enviar e recortar" },
  { label: "Categoria", description: "Escolher contexto" },
  { label: "Tipo", description: "Gratuito ou pago" },
  { label: "Público", description: "Público-alvo" },
  { label: "Revisar", description: "Confirmar detalhes" },
];

const PHOTO_STEPS = [
  { label: "Categoria", description: "Escolher contexto" },
  { label: "Tipo", description: "Gratuito ou pago" },
  { label: "Público", description: "Público-alvo" },
  { label: "Revisar", description: "Confirmar detalhes" },
];

export default function Create({ user, photo }: Props) {
  const hasPhoto = !!photo;
  const firstStep = hasPhoto ? 2 : 1;
  const [currentStep, setCurrentStep] = useState(firstStep);

  const {
    formData,
    isSubmitting,
    error,
    setError,
    setImageFile,
    setCroppedFile,
    setCategory,
    setIsPaid,
    setVotesTarget,
    setDemographicsGender,
    setDemographicsAge,
    submitForm,
  } = useSubmissionForm(photo ? { photoId: photo.id } : undefined);

  // Last step: free=4(review mapped to 5), paid=5
  const lastStep = formData.isPaid ? 5 : 4;
  // Total steps for indicator display
  const indicatorTotal = hasPhoto
    ? formData.isPaid
      ? 4
      : 3
    : formData.isPaid
      ? 5
      : 4;

  const resolveStepTitle = (step: number) => {
    // Step 4 when free is actually the review step (5)
    if (step === 4 && !formData.isPaid) return STEP_TITLES[5];
    return STEP_TITLES[step];
  };

  const resolveStepDescription = (step: number) => {
    if (step === 4 && !formData.isPaid) return STEP_DESCRIPTIONS[5];
    return STEP_DESCRIPTIONS[step];
  };

  useEffect(() => {
    posthog.capture("submission_step_viewed", {
      step: currentStep,
      step_name: resolveStepTitle(currentStep),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const handleNext = async () => {
    setError(null);

    if (currentStep === 1) {
      if (!formData.imageFile) {
        setError("Envie uma imagem antes de continuar");
        return;
      }
      if (!formData.croppedFile) {
        setError("Recorte a imagem antes de continuar");
        return;
      }
      posthog.capture("submission_step_completed", {
        step: 1,
        step_name: STEP_TITLES[1],
      });
      setCurrentStep(2);
      return;
    }

    if (currentStep === 2 && !formData.category) {
      setError("Selecione uma categoria antes de continuar");
      return;
    }

    const isLastStep =
      currentStep === lastStep || (currentStep === 4 && !formData.isPaid);

    if (isLastStep) {
      posthog.capture("submission_step_completed", {
        step: currentStep,
        step_name: resolveStepTitle(currentStep),
      });
      await submitForm();
      return;
    }

    posthog.capture("submission_step_completed", {
      step: currentStep,
      step_name: resolveStepTitle(currentStep),
    });

    if (currentStep === 3) {
      if (formData.isPaid) {
        setCurrentStep(4);
      } else {
        // Free: skip to review (step 4, which renders review)
        setCurrentStep(4);
      }
      return;
    }

    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    setError(null);
    if (currentStep === 4 && !formData.isPaid) {
      // Review (free) → back to Type
      setCurrentStep(3);
    } else if (currentStep > firstStep) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClearImage = () => {
    setImageFile(null);
  };

  const cancelPath = "/submissions";

  // Map absolute step to indicator position (1-based)
  const indicatorStep = hasPhoto ? currentStep - 1 : currentStep;

  const isReviewStep =
    (currentStep === 4 && !formData.isPaid) || currentStep === 5;

  const stepContent = (
    <>
      {error && (
        <Alert variant="error" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {currentStep === 1 && !hasPhoto && (
        <ImageUploadStep
          imageFile={formData.imageFile}
          onFileSelect={setImageFile}
          onCropReady={setCroppedFile}
          onClearImage={handleClearImage}
        />
      )}

      {currentStep === 2 && (
        <CategoryStep
          selectedCategory={formData.category}
          onSelect={(category) => {
            setError(null);
            setCategory(category);
          }}
        />
      )}

      {currentStep === 3 && (
        <SubmissionTypeStep
          isPaid={formData.isPaid}
          votesTarget={formData.votesTarget}
          onIsPaidChange={(isPaid) => {
            setError(null);
            setIsPaid(isPaid);
          }}
          onVotesTargetChange={setVotesTarget}
        />
      )}

      {currentStep === 4 && formData.isPaid && formData.demographics && (
        <DemographicsStep
          demographics={formData.demographics}
          onGenderChange={setDemographicsGender}
          onAgeChange={setDemographicsAge}
        />
      )}

      {isReviewStep && (
        <ReviewStep formData={formData} photoUrl={photo?.photoUrl} />
      )}
    </>
  );

  const navButtons = (
    <div className="flex items-center justify-between border-t border-border pt-4 md:pt-6">
      <Button
        variant="ghost"
        onClick={
          currentStep === firstStep
            ? () => router.visit(cancelPath)
            : handleBack
        }
        className="gap-2 text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft size={16} />
        {currentStep === firstStep ? "Cancelar" : "Voltar"}
      </Button>

      <Button onClick={handleNext} disabled={isSubmitting} className="gap-2">
        {isReviewStep
          ? isSubmitting
            ? "Enviando..."
            : "Enviar Foto"
          : "Continuar"}
        <ArrowRight size={16} />
      </Button>
    </div>
  );

  return (
    <Layout title={hasPhoto ? "Enviar para Votação" : "Nova Foto"} user={user}>
      {/* Mobile layout: full-screen flat */}
      <div className="flex min-h-[calc(100dvh-8rem)] flex-col px-4 py-2 md:hidden">
        <StepIndicator
          currentStep={indicatorStep}
          totalSteps={indicatorTotal}
          steps={hasPhoto ? PHOTO_STEPS : FULL_STEPS}
        />

        <div className="mb-1 text-center">
          <h2 className="text-lg font-semibold">
            {resolveStepTitle(currentStep)}
          </h2>
          <p className="text-sm text-muted-foreground">
            {resolveStepDescription(currentStep)}
          </p>
        </div>

        <div className="flex-1">{stepContent}</div>

        {navButtons}
      </div>

      {/* Desktop layout: contained card */}
      <div className="mx-auto hidden max-w-4xl px-4 py-2 md:block">
        <StepIndicator
          currentStep={indicatorStep}
          totalSteps={indicatorTotal}
          steps={hasPhoto ? PHOTO_STEPS : FULL_STEPS}
        />

        <Card className="border-border bg-card backdrop-blur">
          <CardHeader>
            <CardTitle className="text-xl">
              {resolveStepTitle(currentStep)}
            </CardTitle>
            <CardDescription>
              {resolveStepDescription(currentStep)}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {stepContent}
            {navButtons}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
